// @ts-strict-ignore
import React, { useEffect } from 'react'
import {
	Language,
	convertLanguage,
	COLLECTION_ITEM_SUBTYPE_LIST,
	COLLECTION_ITEM_SUBTYPE_MENU,
	COLLECTION_ITEM_SUBTYPE_TOUR,
	COLLECTION_ITEM_SUBTYPE_STORY,
	COLLECTION_ITEM_SUBTYPE_WEB,
	COLLECTION_ITEM_SUBTYPE_PANORAMA,
	AppMetaData,
} from 'mytours-read-common'

import { DEFAULT_APP_INFO, getAccountId, loadCompletedOnboardingStep, saveCompletedOnboardingStep, saveDefaultLanguageCode } from '../settings/functions'
import NavigationService from '../navigation/NavigationService'
import Settings from '../settings/functions'

import strings from '../../strings'
import CollectionTourComponent from '../collection/components/CollectionTour'
import ScreenPanoramaComponent from '../screen/components/ScreenPanorama'
import ScreenStoryComponent from '../screen/components/ScreenStory'
import ScreenWebComponent from '../screen/components/ScreenWeb'
import { LanguageCode, OnboardingStep } from 'mytours-read-api-typescript'
import CollectionListComponent from '../collection/components/CollectionList'
import { getLanguageCodeFromUrl, isAvailableLanguage } from '../navigation/utils'

export interface RouteProps {
	locale?: string
	tabIndex?: string
}

export const handleAppUpgrade = (projectId: string): Promise<boolean> => {
	console.log('handleAppUpgrade', projectId)
	return Promise.resolve(true)
}

export const useOnboardingGate = (loadForm: (id: number) => void) => {
	const [loaded, setLoaded] = React.useState(false)
	const [loadedDisk, setLoadedDisk] = React.useState(false)

	const [steps, setSteps] = React.useState<OnboardingStep[]>([])
	const [completeSteps, setCompleteSteps] = React.useState<OnboardingStep[]>([])
	const [incompleteSteps, setIncompleteSteps] = React.useState<OnboardingStep[]>([])

	// Check steps are actually incomplete
	useEffect(() => {
		(async () => {
			if (!loaded || loadedDisk)
				return

			const newCompleteSteps: OnboardingStep[] = []
			const newIncompleteSteps: OnboardingStep[] = []

			for (const step of steps) {
				const completed = await loadCompletedOnboardingStep(DEFAULT_APP_INFO.projectId, step.id)
				if (completed) newCompleteSteps.push(step)
				else newIncompleteSteps.push(step)
			}

			setCompleteSteps(newCompleteSteps)
			setIncompleteSteps(newIncompleteSteps)

			// Set state
			setLoadedDisk(true)
		})()
	}, [loaded, loadedDisk, completeSteps, incompleteSteps, steps])

	return {
		loaded: loaded && loadedDisk,
		done: loaded && loadedDisk && incompleteSteps.length === 0,
		steps,
		incompleteSteps,
		completeSteps,
		init: (steps: OnboardingStep[]) => {
			if (loaded)
				return

			// Save steps
			steps = steps.sort((a, b) => a.position - b.position)
			setSteps([...steps])
			setIncompleteSteps([...steps])

			// Download required step data
			steps.forEach(form => loadForm(form.form_id))

			setLoaded(true)
		},
		completeStep: (stepId: number) => {
			setCompleteSteps([...completeSteps, steps.find(step => step.id === stepId)])
			saveCompletedOnboardingStep(DEFAULT_APP_INFO.projectId, stepId)

			const newIncompleteSteps = incompleteSteps.filter(step => step.id !== stepId)
			setIncompleteSteps(newIncompleteSteps)
		}
	}
}

export const LanguageGate = {
	_loaded: false,
	currentLocale: null,
	init: selectLanguageCommon => {
		if (LanguageGate._loaded) {
			return
		}

		LanguageGate.selectLanguageCommon = selectLanguageCommon
		LanguageGate._loaded = true
	},
	selectLanguageCommon: (language: Language) => {
		console.log('language', language)
	},
	selectLanguage: (language: Language) => {
		if (!language) {
			return
		}

		if (LanguageGate.currentLocale !== language.code) {
			LanguageGate.currentLocale = language.code
			LanguageGate.selectLanguageCommon(language)
			saveDefaultLanguageCode(DEFAULT_APP_INFO.projectId, language.code)
		}
	},
}

export const saveDefaultLanguageConfig = (
	languageCode: string,
	appMetaData: AppMetaData,
	storedLanguageCode?: LanguageCode,
) => {
	saveAppLanguage({ languageCode, appMetaData, storedLanguageCode })
}

export const selectAppLanguage = (appMetaData: AppMetaData, storedLanguageCode?: LanguageCode) => {
	const { primary_language: primaryLanguage, supported_languages: supportedLanguages } = appMetaData

	let languageCode: string

	// get language code from location string
	const languageParam = getLanguageCodeFromUrl(supportedLanguages)

	if (languageParam) {
		// set language code from location string
		languageCode = languageParam
	} else if (storedLanguageCode) {
		if (supportedLanguages.includes(storedLanguageCode)) {
			// set language code from local storage
			languageCode = storedLanguageCode
		} else {
			languageCode = primaryLanguage
		}
	} else if (supportedLanguages.length === 1) {
		languageCode = primaryLanguage
	}

	// if code exist then setup app interface
	if (languageCode) {
		strings.setLanguage(languageCode)
		Settings.appLanguage.setCurrentLocale(languageCode)
		LanguageGate.selectLanguage(convertLanguage(languageCode))
	}
}

export const saveAppLanguage = async ({
	params,
	callback,
	languageCode,
	appMetaData,
	storedLanguageCode,
}: {
	params?: RouteProps
	callback?: () => void
	languageCode?: string
	appMetaData: AppMetaData
	storedLanguageCode?: LanguageCode
}): Promise<void> => {
	let locale, tabIndex

	if (params) {
		locale = params.locale
		tabIndex = params.tabIndex
	}

	if (languageCode) {
		locale = languageCode
	}

	if (tabIndex) {
		NavigationService.navigationStack.setActiveTabIndex(tabIndex)
	}

	if (LanguageGate.currentLocale && LanguageGate.currentLocale === locale) {
		callback?.()
		return
	}

	if (isAvailableLanguage(locale)) {
		Settings.appLanguage.setCurrentLocale(locale)
		LanguageGate.selectLanguage(convertLanguage(locale))
	} else {
		selectAppLanguage(appMetaData, storedLanguageCode)
	}

	callback?.()
}

export const getCurrentTimestamp = (custom?: boolean) => {
	if (custom) {
		const date = new Date()
		return Number(`${date.getFullYear()}${date.getMonth()}${date.getDate()}`)
	}

	return Math.floor(Date.now() / 1000)
}

interface ComponentParams {
	id: number
	version?: number
	accountId?: number
}
export const getComponentParams = (type: string, params, state): ComponentParams => ({
	id: params?.id ? Number(params.id) : null,
	version: state?.[`${type}Version`] ? state[`${type}Version`] : undefined,
	accountId: state?.accountId || (params?.accountId ? Number(params.accountId) : undefined),
})

export const getPageComponent = (
	subtype: string,
	id: number,
	version: number,
	onBackAction?: (params?: any) => void,
	isInsideModal?: boolean,
	accountId?: number
): JSX.Element | undefined => {
	if (!accountId) {
		accountId = getAccountId()
	}
	switch (subtype) {
		case COLLECTION_ITEM_SUBTYPE_LIST:
		case COLLECTION_ITEM_SUBTYPE_MENU:
			return (
				<CollectionListComponent
					collectionId={id}
					collectionVersion={version}
					onBackAction={onBackAction}
					isInsideModal={isInsideModal}
					accountId={accountId}
				/>
			)
		case COLLECTION_ITEM_SUBTYPE_TOUR:
			return (
				<CollectionTourComponent
					collectionId={id}
					collectionVersion={version}
					onBackAction={onBackAction}
					isInsideModal={isInsideModal}
					accountId={accountId}
				/>
			)
		case COLLECTION_ITEM_SUBTYPE_STORY:
			return (
				<ScreenStoryComponent
					itemId={id}
					itemVersion={version}
					onBackAction={onBackAction}
					isInsideModal={isInsideModal}
					accountId={accountId}
				/>
			)
		case COLLECTION_ITEM_SUBTYPE_WEB:
			return (
				<ScreenWebComponent
					itemId={id}
					itemVersion={version}
					onBackAction={onBackAction}
					isInsideModal={isInsideModal}
					accountId={accountId}
				/>
			)
		case COLLECTION_ITEM_SUBTYPE_PANORAMA:
			return (
				<ScreenPanoramaComponent
					itemId={id}
					itemVersion={version}
					onBackAction={onBackAction}
					isInsideModal={isInsideModal}
					accountId={accountId}
				/>
			)
		default:
			return undefined
	}
}

interface TabsBreakpoints {
	isSmallMobile: boolean
	isMobile: boolean
	isBigMobile: boolean
}
export const tabsCount = (tabsLength: number, { isSmallMobile, isMobile, isBigMobile }: TabsBreakpoints): number => {
	let result = 5

	if (isSmallMobile /* 320 < * */) {
		result = 2
	} else if (isMobile /* 320 --- 412 */) {
		result = 3
	} else if (isBigMobile /* * > 640 */) {
		result = 4
	}

	// if the `more` menu has only one item then hide `more` button and show all tabs without hidden items
	if (tabsLength - result === 1) {
		result = result + 1
	}

	return result
}
